import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import legalContentService, {
  type LegalContent,
} from "@/services/legalContentService";

export function Footer() {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showSalesDialog, setShowSalesDialog] = useState(false);
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const [showLegalNoticesDialog, setShowLegalNoticesDialog] = useState(false);

  // Legal content state
  const [legalContents, setLegalContents] = useState<
    Record<string, LegalContent>
  >({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLegalContents();
  }, []);

  const fetchLegalContents = async () => {
    try {
      const contents = await legalContentService.getAllLegalContents();
      const contentMap = contents.reduce(
        (acc, content) => {
          acc[content.type] = content;
          return acc;
        },
        {} as Record<string, LegalContent>,
      );
      setLegalContents(contentMap);
    } catch (error) {
      console.error("Error fetching legal contents:", error);
      // Fallback to static content if API fails
      setLegalContents({});
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedContent = (content: LegalContent) => {
    return i18n.language === "fr" ? content.frContent : content.enContent;
  };

  const getLocalizedTitle = (type: string) => {
    switch (type) {
      case "terms":
        return t("legal.termsAndConditions");
      case "privacy":
        return t("legal.privacyPolicy");
      case "sales":
        return t("legal.salesConditions");
      case "legal_notices":
        return t("legal.legalNotices");
      case "faq":
        return t("legal.faq");
      default:
        return type;
    }
  };

  const renderLegalDialog = (
    type: string,
    isOpen: boolean,
    setIsOpen: (open: boolean) => void,
  ) => {
    const content = legalContents[type];
    if (!content) return null;

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{getLocalizedTitle(type)}</DialogTitle>
            <DialogDescription>{t("legal.pleaseRead")}</DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <div className="prose dark:prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {getLocalizedContent(content)}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <footer className="border-t bg-background">
        <div className="py-4 px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            {/* Left: Copyright */}
            <p className="text-center md:text-left text-sm text-muted-foreground">
              &copy; {currentYear} {import.meta.env.VITE_APP_NAME}.{" "}
              {t("home.rightsReserved")}.
            </p>

            {/* Center: Powered by Kiam Tech */}
            <div className="text-center">
              <a
                href="https://www.kiamtech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t("home.poweredBy")}{" "}
                <span className="font-semibold">Kiam Tech</span>
              </a>
            </div>

            {/* Right: Legal links */}
            <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
              {!loading && (
                <>
                  {legalContents.terms && (
                    <button
                      onClick={() => setShowTermsDialog(true)}
                      className="hover:text-primary underline transition-colors"
                    >
                      {t("legal.termsAndConditions")}
                    </button>
                  )}
                  {legalContents.privacy && (
                    <button
                      onClick={() => setShowPrivacyDialog(true)}
                      className="hover:text-primary underline transition-colors"
                    >
                      {t("legal.privacyPolicy")}
                    </button>
                  )}
                  {legalContents.sales && (
                    <button
                      onClick={() => setShowSalesDialog(true)}
                      className="hover:text-primary underline transition-colors"
                    >
                      {t("legal.salesConditions")}
                    </button>
                  )}
                  {legalContents.legal_notices && (
                    <button
                      onClick={() => setShowLegalNoticesDialog(true)}
                      className="hover:text-primary underline transition-colors"
                    >
                      {t("legal.legalNotices")}
                    </button>
                  )}
                  {legalContents.faq && (
                    <button
                      onClick={() => setShowFaqDialog(true)}
                      className="hover:text-primary underline transition-colors"
                    >
                      {t("legal.faq")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Render legal dialogs */}
      {renderLegalDialog("terms", showTermsDialog, setShowTermsDialog)}
      {renderLegalDialog("privacy", showPrivacyDialog, setShowPrivacyDialog)}
      {renderLegalDialog("sales", showSalesDialog, setShowSalesDialog)}
      {renderLegalDialog(
        "legal_notices",
        showLegalNoticesDialog,
        setShowLegalNoticesDialog,
      )}
      {renderLegalDialog("faq", showFaqDialog, setShowFaqDialog)}
    </>
  );
}
